import type { ReactElement } from 'react';
import { useShow, useGo } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import {
  Typography,
  Descriptions,
  Tag,
  Timeline,
  Card,
  Space,
  Button,
} from 'antd';
import { ShoppingCartOutlined } from '@ant-design/icons';

import type {
  AdminCheckpointDetail,
  CheckpointEntry,
  OrderStatus,
} from '@/types';

const { Title, Text } = Typography;

// ---------------------------------------------------------------------------
// Status color mapping (consistent with OrderList)
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<OrderStatus, string> = {
  INIT: 'default',
  STARTED: 'gold',
  PAYMENT_PENDING: 'gold',
  PAYMENT_COMPLETED: 'blue',
  PAYMENT_FAILED: 'red',
  GENERATING: 'processing',
  GENERATING_PLAN: 'processing',
  GENERATING_CHARACTER_SHEET: 'processing',
  GENERATING_FINAL: 'processing',
  PLAN_REVIEW: 'orange',
  CHARACTER_SHEET_REVIEW: 'orange',
  GENERATION_COMPLETE: 'green',
  GENERATION_FAILED: 'red',
  PREVIEW_READY: 'green',
  CANCELLED: 'default',
  PRINT_JOB_SUBMITTED: 'cyan',
  SHIPPED: 'green',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract the node name from LangGraph checkpoint metadata */
function getNodeName(metadata: Record<string, unknown>): string | null {
  if (typeof metadata.source === 'string' && metadata.source) {
    return metadata.source;
  }
  if (typeof metadata.step === 'number') {
    return `Step ${metadata.step}`;
  }
  return null;
}

/** Map the checkpoint namespace to a human-readable label */
function formatNamespace(ns: string): string {
  if (ns === '') return 'root';
  return ns;
}

// ---------------------------------------------------------------------------
// Timeline item renderer
// ---------------------------------------------------------------------------

function CheckpointTimelineItem({
  checkpoint,
}: {
  checkpoint: CheckpointEntry;
}): ReactElement {
  const nodeName = getNodeName(checkpoint.metadata);

  return (
    <div>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Space size="small" wrap>
          <Text strong style={{ fontSize: 13 }}>
            {checkpoint.checkpointId.slice(0, 12)}...
          </Text>
          <Tag>{formatNamespace(checkpoint.checkpointNs)}</Tag>
          {nodeName && <Tag color="blue">{nodeName}</Tag>}
        </Space>

        {checkpoint.parentCheckpointId && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            Parent: {checkpoint.parentCheckpointId.slice(0, 12)}...
          </Text>
        )}

        {checkpoint.channelKeys.length > 0 && (
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Channel keys:{' '}
            </Text>
            <Space size={[4, 4]} wrap>
              {checkpoint.channelKeys.map((key) => (
                <Tag key={key} color="geekblue" style={{ fontSize: 11 }}>
                  {key}
                </Tag>
              ))}
            </Space>
          </div>
        )}

        {Object.keys(checkpoint.metadata).length > 0 && (
          <details style={{ marginTop: 4 }}>
            <summary style={{ fontSize: 11, cursor: 'pointer', color: '#888' }}>
              Metadata
            </summary>
            <pre
              style={{
                maxHeight: 200,
                overflow: 'auto',
                fontSize: 11,
                background: '#f5f5f5',
                padding: 8,
                borderRadius: 4,
                margin: '4px 0 0 0',
              }}
            >
              {JSON.stringify(checkpoint.metadata, null, 2)}
            </pre>
          </details>
        )}
      </Space>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CheckpointShow(): ReactElement {
  const go = useGo();
  const { query } = useShow<AdminCheckpointDetail>({
    resource: 'checkpoints',
  });
  const record = query.data?.data;
  const isLoading = query.isLoading;

  return (
    <Show isLoading={isLoading}>
      {record && (
        <>
          {/* Thread info header */}
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Thread ID">
              <Text copyable>{record.threadId}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Order ID">
              {record.orderId ? (
                <Button
                  type="link"
                  size="small"
                  icon={<ShoppingCartOutlined />}
                  onClick={() =>
                    go({
                      to: {
                        resource: 'orders',
                        action: 'show',
                        id: record.orderId as string,
                      },
                    })
                  }
                  style={{ padding: 0 }}
                >
                  {record.orderId}
                </Button>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Order Status">
              {record.orderStatus ? (
                <Tag color={STATUS_COLORS[record.orderStatus] ?? 'default'}>
                  {record.orderStatus}
                </Tag>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Total Checkpoints">
              {record.checkpoints.length}
            </Descriptions.Item>
          </Descriptions>

          {/* Checkpoint timeline */}
          <Title level={5} style={{ marginTop: 24 }}>
            Checkpoint Timeline
          </Title>
          <Card size="small">
            <Timeline
              mode="left"
              items={record.checkpoints.map((cp) => ({
                key: `${cp.checkpointId}-${cp.checkpointNs}`,
                color: cp.checkpointNs === '' ? 'blue' : 'gray',
                children: <CheckpointTimelineItem checkpoint={cp} />,
              }))}
            />
          </Card>
        </>
      )}
    </Show>
  );
}
